import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ChevronDown, Layers, Lock, MoreHorizontal, Pencil, Plus, RefreshCcw, Search, Swords,
  Trash2, X,
} from 'lucide-react'
import { fetchMaps, type MapInfo } from '@/lib/maps'
import { editorMenu } from './editorUi'
import { tacticalFilter } from '@/lib/mapView'
import { strategyEditorPath, type StrategyKind } from '@/lib/strategyKinds'
import { useLanguage } from '@/contexts/LanguageContext'
import { useAuth } from '@/contexts/AuthContext'

/**
 * Bir bölümün strateji kitaplığı — `match` ve `playbook` bunu paylaşır.
 *
 * İki bölüm aynı tabloda, aynı editörde ve aynı slayt ağacında durduğu için
 * kitaplık da tek bileşendir; ayrışan şey backend'in satır başına döndürdüğü
 * `can_edit` ve bölümün kim tarafından açılabildiğidir (`canCreate`).
 *
 * Eski gezinme sol kenardaki daraltılmış bir listeydi: strateji adı 18 karakterde
 * kesiliyor, hangi harita olduğu küçük bir etikete sığıyor ve kim açmış hiç
 * görünmüyordu. Izgara aynı yere haritanın kendi görüntüsünü koyuyor — bir maç
 * planını bulmanın en hızlı yolu haritayı tanımaktır.
 */

export interface StrategyRow {
  id: number
  title: string
  kind: StrategyKind
  map_name: string
  match_event_id: string
  parent_id: number | null
  created_by: string
  created_by_name: string
  created_at: number
  updated_at: number
  can_edit: boolean
}

interface MatchItem { id: string; title: string }
interface MatchApiResponse { event_id: string; event: { title: string } }

function formatDate(ts: number): string {
  if (!ts) return '-'
  return new Date(ts * 1000).toLocaleDateString('tr-TR', {
    day: '2-digit', month: 'short', year: 'numeric',
  })
}

const inputCls =
  'w-full rounded-sm border border-white/10 bg-black/40 px-2.5 py-1.5 text-xs text-foreground ' +
  'placeholder:text-muted-foreground/40 focus:border-primary/40 focus:outline-none'

/** "Slayt 3" — slayt adı sıra numarasından türer, düğme etiketinden değil. */
const slideName = (T: { strategy: { library: { slideName: string } } }, n: number) =>
  T.strategy.library.slideName.replace('{n}', String(n))

const fieldLabelCls =
  'mb-1 block text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/60'

// ── Create panel ────────────────────────────────────────────────────────────
/**
 * Açma formu ızgaranın üstünde açılır bir şerittir, ayrı bir sayfa değil:
 * strateji açmak bir adım (harita seç, ad ver), o yüzden listeyi terk etmeyi
 * hak etmiyor. Maç bağlama alanı **yalnızca maç stratejilerinde** var — taktik
 * defterindeki bir fikir bir maça ait değil.
 */
function CreatePanel({ kind, maps, onClose, onCreated }: {
  kind: StrategyKind
  maps: MapInfo[]
  onClose: () => void
  onCreated: (id: number, mapName: string) => void
}) {
  const { T } = useLanguage()
  const [title, setTitle] = useState('')
  const [mapName, setMapName] = useState(maps[0]?.name ?? '')
  const [matchId, setMatchId] = useState('')
  const [matches, setMatches] = useState<MatchItem[]>([])
  const [busy, setBusy] = useState(false)

  useEffect(() => { if (!mapName && maps[0]) setMapName(maps[0].name) }, [maps, mapName])

  useEffect(() => {
    if (kind !== 'match') return
    fetch('/api/matches', { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then((d: MatchApiResponse[]) =>
        setMatches(d.map(m => ({ id: m.event_id, title: m.event?.title ?? m.event_id }))))
      .catch(() => {})
  }, [kind])

  const submit = async () => {
    if (!mapName || busy) return
    setBusy(true)
    try {
      const res = await fetch('/api/strategies', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: title.trim() || T.strategy.untitled,
          map_name: mapName,
          match_event_id: kind === 'match' ? matchId : '',
          kind,
        }),
      })
      if (res.ok) {
        const { id } = await res.json()
        // Strateji boş bir kapsayıcı olarak açılmaz: ilk slaytı burada
        // yaratılır, editör de onu açar (StrategyMap.handleSelect). Slayt
        // isteği patlarsa yine gidilir — kök kendisi açılır, bir şey kırılmaz.
        await fetch('/api/strategies', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title: slideName(T, 1), parent_id: id }),
        }).catch(() => {})
        onCreated(id, mapName)
      }
    } catch { /* ignore */ }
    setBusy(false)
  }

  return (
    <div className="mb-5 rounded-sm border border-white/10 bg-black/25 p-4 backdrop-blur-sm">
      <div className="mb-3 flex items-center gap-2">
        <span aria-hidden className="h-px w-5 bg-primary" />
        <h3 className="flex-1 font-display text-xs font-bold uppercase tracking-[0.18em]">
          {kind === 'match' ? T.strategy.library.newMatchStrategy : T.strategy.library.newPlaybookEntry}
        </h3>
        <button
          onClick={onClose}
          className="text-muted-foreground/50 transition-colors hover:text-foreground"
          aria-label={T.strategy.cancel}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className={`grid gap-3 ${kind === 'match' ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
        <div>
          <label className={fieldLabelCls}>{T.strategy.title}</label>
          <input
            autoFocus
            value={title}
            onChange={e => setTitle(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') submit() }}
            placeholder={T.strategy.titlePlaceholder}
            className={inputCls}
          />
        </div>
        <div>
          <label className={fieldLabelCls}>{T.strategy.map}</label>
          <select value={mapName} onChange={e => setMapName(e.target.value)} className={`${inputCls} cursor-pointer`}>
            {maps.length === 0 && <option value="">{T.strategy.selectMap}</option>}
            {maps.map(m => <option key={m.name} value={m.name}>{m.label}</option>)}
          </select>
        </div>
        {kind === 'match' && (
          <div>
            <label className={fieldLabelCls}>
              {T.strategy.match}{' '}
              <span className="font-normal normal-case tracking-normal opacity-60">({T.strategy.optional})</span>
            </label>
            <select value={matchId} onChange={e => setMatchId(e.target.value)} className={`${inputCls} cursor-pointer`}>
              <option value="">{T.strategy.noMatch}</option>
              {matches.map(m => <option key={m.id} value={m.id}>{m.title}</option>)}
            </select>
          </div>
        )}
      </div>

      <button
        onClick={submit}
        disabled={!mapName || busy}
        className="mt-3 rounded-sm bg-primary px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-primary-foreground transition-colors hover:bg-primary/85 disabled:opacity-40"
      >
        {busy ? T.strategy.creating : T.strategy.create}
      </button>
      {!mapName && maps.length === 0 && (
        <p className="mt-2 text-[11px] text-muted-foreground/60">{T.strategy.library.noMapPicked}</p>
      )}
    </div>
  )
}

// ── Strategy card ───────────────────────────────────────────────────────────
function StrategyCard({ row, map, matchTitle, onOpen, onRename, onDelete }: {
  row: StrategyRow
  map?: MapInfo
  matchTitle?: string
  onOpen: (id: number) => void
  onRename: (id: number, title: string) => void
  onDelete: (id: number) => void
}) {
  const { T } = useLanguage()
  const { user } = useAuth()
  const [menuOpen, setMenuOpen] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [renaming, setRenaming] = useState(false)
  const [draft, setDraft] = useState(row.title)
  const [slidesOpen, setSlidesOpen] = useState(false)
  const [slides, setSlides] = useState<StrategyRow[] | null>(null)
  const [addingSlide, setAddingSlide] = useState(false)
  const renameRef = useRef<HTMLInputElement>(null)

  // Slaytlar kart açılınca çekilir: ızgaradaki her kart için peşin bir istek
  // açmak, listeyi görmek için yirmi çağrı demekti.
  const loadSlides = useCallback(async () => {
    try {
      const res = await fetch(`/api/strategies/${row.id}/configs`, { credentials: 'include' })
      if (res.ok) setSlides(await res.json())
    } catch { /* ignore */ }
  }, [row.id])

  const toggleSlides = () => {
    setSlidesOpen(o => !o)
    if (slides === null) loadSlides()
  }

  const submitRename = async () => {
    const next = draft.trim()
    setRenaming(false)
    if (!next || next === row.title) return
    try {
      await fetch(`/api/strategies/${row.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: next }),
      })
      onRename(row.id, next)
    } catch { /* ignore */ }
  }

  const addSlide = async () => {
    setAddingSlide(true)
    try {
      const res = await fetch('/api/strategies', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: slideName(T, (slides?.length ?? 0) + 1), parent_id: row.id }),
      })
      if (res.ok) {
        const { id } = await res.json()
        onOpen(id)
      }
    } catch { /* ignore */ }
    setAddingSlide(false)
  }

  const slideCount = slides?.length
  const mine = user?.id === row.created_by

  return (
    // ⚠️ Kartın kökü `overflow-hidden` DEĞİL: "..." menüsü kartın dibinden
    // taşar ve kırpılırsa kartın içinde kalıp okunamaz hale gelirdi. Şeridin
    // hover büyümesini kırpan kendi `overflow-hidden`'ı zaten var, üstteki
    // yuvarlatma da oraya (`rounded-t-sm`) geçti.
    <div className="group flex flex-col rounded-sm border border-white/10 bg-white/[0.035] transition-colors hover:border-white/25">
      {/* Harita şeridi — kartın kimliği.
          Oyunun tanıtım görseli kullanılır: 112 pikselde bir maç planını
          tanımanın en hızlı yolu, tepeden gri bir kare değil haritanın
          manzarasıdır.

          ⚠️ Görselin **sol altında haritanın adı gömülü**. Şerit 16:9'dan daha
          geniş olduğu için kırpma şart ve adın kesilmiş yarısı bozuk görünürdü;
          bu yüzden kadraj yukarı çekilir (`objectPosition`) ve alttaki koyu
          perde kalanı örter. Kartın kendi harita etiketi zaten o köşede duruyor,
          yani ad kaybolmuyor — yeri değişiyor.

          Görseli olmayan harita eski davranışına, taktiksel filtreli `thumb`'a
          düşer. */}
      <button
        type="button"
        onClick={() => onOpen(row.id)}
        className="relative block h-28 w-full overflow-hidden rounded-t-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {(map?.hero ?? map?.thumb) && (
          <img
            src={map!.hero ?? map!.thumb}
            alt=""
            aria-hidden
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.05]"
            style={{
              objectPosition: map!.hero ? 'center 32%' : undefined,
              filter: !map!.hero && map!.luma ? tacticalFilter(map!.luma) : undefined,
            }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-black/20" />
        <div className="relative flex h-full flex-col justify-end gap-1 p-3">
          <div className="flex items-center gap-1.5">
            <span className="rounded-sm bg-black/60 px-1.5 py-0.5 font-display text-[10px] font-bold uppercase tracking-[0.14em] text-white/80 backdrop-blur-sm">
              {map?.label ?? row.map_name}
            </span>
            {matchTitle && (
              <span className="flex items-center gap-1 truncate rounded-sm bg-primary/25 px-1.5 py-0.5 text-[10px] font-semibold text-white/90">
                <Swords className="h-2.5 w-2.5 shrink-0" />
                <span className="truncate">{matchTitle}</span>
              </span>
            )}
          </div>
        </div>
      </button>

      {/* Gövde */}
      <div className="flex flex-1 flex-col p-3">
        <div className="flex items-start gap-1.5">
          {renaming ? (
            <input
              ref={renameRef}
              autoFocus
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onBlur={submitRename}
              onKeyDown={e => {
                if (e.key === 'Enter') submitRename()
                if (e.key === 'Escape') { setDraft(row.title); setRenaming(false) }
              }}
              className="min-w-0 flex-1 rounded-sm border border-white/20 bg-black/50 px-1.5 py-0.5 text-sm font-semibold focus:border-primary/50 focus:outline-none"
            />
          ) : (
            <button
              onClick={() => onOpen(row.id)}
              className="min-w-0 flex-1 truncate text-left font-display text-sm font-bold tracking-[0.04em] transition-colors hover:text-white"
              title={row.title}
            >
              {row.title || T.strategy.untitled}
            </button>
          )}

          {row.can_edit && (
            <div className="relative shrink-0">
              <button
                onClick={() => { setMenuOpen(o => !o); setConfirming(false) }}
                className="rounded-sm p-0.5 text-muted-foreground/40 transition-colors hover:bg-white/10 hover:text-foreground"
                aria-label={T.strategy.rename}
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-20" onClick={() => { setMenuOpen(false); setConfirming(false) }} />
                  <div className={`absolute right-0 top-full z-30 mt-1 min-w-[170px] py-1 ${editorMenu}`}>
                    {confirming ? (
                      <div className="px-3 py-2">
                        <p className="mb-2 text-[11px] text-muted-foreground">{T.strategy.deleteStrategy}</p>
                        <div className="flex gap-1.5">
                          <button
                            onClick={() => { setMenuOpen(false); onDelete(row.id) }}
                            className="flex-1 rounded-sm bg-red-500/20 px-2 py-1 text-[11px] font-semibold text-red-400 transition-colors hover:bg-red-500/30"
                          >
                            {T.strategy.delete}
                          </button>
                          <button
                            onClick={() => { setConfirming(false); setMenuOpen(false) }}
                            className="flex-1 rounded-sm bg-white/5 px-2 py-1 text-[11px] font-semibold text-muted-foreground transition-colors hover:bg-white/10"
                          >
                            {T.strategy.cancel}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <button
                          onClick={() => { setMenuOpen(false); setDraft(row.title); setRenaming(true) }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-xs text-foreground/80 transition-colors hover:bg-white/8"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          {T.strategy.rename}
                        </button>
                        <button
                          onClick={() => setConfirming(true)}
                          className="flex w-full items-center gap-2 px-3 py-2 text-xs text-red-400 transition-colors hover:bg-red-500/10"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          {T.strategy.delete}
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-muted-foreground/60">
          <span className="truncate">{row.created_by_name || '-'}</span>
          {mine && (
            <span className="shrink-0 rounded-sm bg-primary/20 px-1 py-px text-[9px] font-semibold uppercase tracking-wider text-primary">
              {T.strategy.library.mine}
            </span>
          )}
          <span className="ml-auto shrink-0 tabular-nums">{formatDate(row.updated_at)}</span>
        </div>

        {/* Slaytlar */}
        <div className="mt-auto pt-3">
          <button
            onClick={toggleSlides}
            className="flex w-full items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/60 transition-colors hover:text-foreground"
          >
            <Layers className="h-3 w-3" />
            <span className="flex-1 text-left">
              {T.strategy.slides}
              {slideCount !== undefined && ` · ${slideCount}`}
            </span>
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${slidesOpen ? '' : '-rotate-90'}`} />
          </button>

          {slidesOpen && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {slides?.map(c => (
                <button
                  key={c.id}
                  onClick={() => onOpen(c.id)}
                  className="max-w-full truncate rounded-sm border border-white/10 bg-white/5 px-2 py-1 text-[11px] text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
                >
                  {c.title}
                </button>
              ))}
              {slides?.length === 0 && (
                <p className="text-[11px] text-muted-foreground/40">{T.strategy.library.noSlides}</p>
              )}
              {row.can_edit && (
                <button
                  onClick={addSlide}
                  disabled={addingSlide}
                  className="flex items-center gap-1 rounded-sm border border-dashed border-white/15 px-2 py-1 text-[11px] font-semibold text-muted-foreground/50 transition-colors hover:border-primary/40 hover:text-primary disabled:opacity-40"
                >
                  <Plus className="h-3 w-3" />
                  {addingSlide ? T.strategy.creating : T.strategy.library.addSlide}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Library ─────────────────────────────────────────────────────────────────
export default function StrategyLibrary({ kind, canCreate }: {
  kind: StrategyKind
  canCreate: boolean
}) {
  const navigate = useNavigate()
  const { T } = useLanguage()
  const [rows, setRows] = useState<StrategyRow[]>([])
  const [maps, setMaps] = useState<MapInfo[]>([])
  const [matchTitles, setMatchTitles] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')
  const [formOpen, setFormOpen] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetch(`/api/strategies?kind=${kind}`, { credentials: 'include' })
      if (res.ok) setRows(await res.json())
    } catch { /* ignore */ }
    setLoading(false)
  }, [kind])

  useEffect(() => { load() }, [load])
  useEffect(() => { fetchMaps().then(setMaps).catch(() => {}) }, [])

  // Bağlı maçın adı yalnızca maç stratejilerinde gösterilir, dolayısıyla
  // yalnızca orada istenir.
  useEffect(() => {
    if (kind !== 'match') return
    fetch('/api/matches', { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then((d: MatchApiResponse[]) => {
        const out: Record<string, string> = {}
        for (const m of d) out[m.event_id] = m.event?.title ?? m.event_id
        setMatchTitles(out)
      })
      .catch(() => {})
  }, [kind])

  const mapByName = useMemo(() => {
    const out: Record<string, MapInfo> = {}
    for (const m of maps) out[m.name] = m
    return out
  }, [maps])

  const visible = useMemo(() => {
    const q = query.trim().toLocaleLowerCase('tr')
    if (!q) return rows
    return rows.filter(r =>
      r.title.toLocaleLowerCase('tr').includes(q) ||
      (mapByName[r.map_name]?.label ?? r.map_name).toLocaleLowerCase('tr').includes(q) ||
      r.created_by_name.toLocaleLowerCase('tr').includes(q))
  }, [rows, query, mapByName])

  /**
   * Editöre hep **kök stratejinin haritasıyla** gidilir: slaytlar haritayı
   * ebeveynden miras aldığı için kartın haritası slaytın da haritasıdır, ve
   * slayt satırı listede durmadığı için kendi `map_name`'ine bakılamaz.
   */
  const open = (mapName: string, id: number) => navigate(strategyEditorPath(kind, mapName, id))

  const handleDelete = async (id: number) => {
    setRows(prev => prev.filter(r => r.id !== id))
    try {
      await fetch(`/api/strategies/${id}`, { method: 'DELETE', credentials: 'include' })
      localStorage.removeItem(`strategy_${id}_ui`)
    } catch { /* ignore */ }
    load()
  }

  return (
    <div>
      {/* Araç şeridi */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[180px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground/40" />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={T.strategy.library.search}
            className={`${inputCls} pl-8`}
          />
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="rounded-sm border border-white/10 p-2 text-muted-foreground/60 transition-colors hover:text-foreground disabled:opacity-40"
          title={T.strategy.refresh}
        >
          <RefreshCcw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
        {canCreate ? (
          <button
            onClick={() => setFormOpen(o => !o)}
            className="flex items-center gap-1.5 rounded-sm bg-primary px-3 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-primary-foreground transition-colors hover:bg-primary/85"
          >
            <Plus className="h-3.5 w-3.5" />
            {kind === 'match' ? T.strategy.library.newMatchStrategy : T.strategy.library.newPlaybookEntry}
          </button>
        ) : (
          <span className="flex items-center gap-1.5 rounded-sm border border-white/10 px-3 py-2 text-[11px] text-muted-foreground/60">
            <Lock className="h-3 w-3 shrink-0" />
            {T.strategy.library.lockedMatch}
          </span>
        )}
      </div>

      {formOpen && canCreate && (
        <CreatePanel
          kind={kind}
          maps={maps}
          onClose={() => setFormOpen(false)}
          onCreated={(id, mapName) => { setFormOpen(false); open(mapName, id) }}
        />
      )}

      {/* Izgara */}
      {loading && rows.length === 0 ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-56 animate-pulse rounded-sm bg-white/5" />
          ))}
        </div>
      ) : visible.length === 0 ? (
        <div className="rounded-sm border border-dashed border-white/10 px-6 py-16 text-center">
          <p className="text-sm text-muted-foreground">
            {rows.length > 0
              ? T.strategy.library.noSearchResult
              : kind === 'match' ? T.strategy.library.emptyMatch : T.strategy.library.emptyPlaybook}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {visible.map(r => (
            <StrategyCard
              key={r.id}
              row={r}
              map={mapByName[r.map_name]}
              matchTitle={r.match_event_id ? matchTitles[r.match_event_id] : undefined}
              onOpen={id => open(r.map_name, id)}
              onRename={(id, title) => setRows(prev => prev.map(x => x.id === id ? { ...x, title } : x))}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </div>
  )
}
